import { Check, Close } from '@mui/icons-material';
import { Box, Icon, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    GridValueFormatterParams,
} from '@mui/x-data-grid-pro';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { ChannelSelector } from 'app/components/form-controls/channel-selector';
import { LocationGroupPicker } from 'app/components/form-controls/location-group-picker';
import { RequireRole } from 'app/components/require-role';
import { RouterLink } from 'app/components/router-link';
import { StorePicker } from 'app/components/store-picker';
import { GeneralConstants } from 'app/constants/general-constants';
import { Role } from 'app/constants/role';
import { PartnerMap } from 'app/model/mcp/partner-map';
import { dialogService } from 'app/services/dialog.service';

import {
    deleteCrisPartnerMap,
    linkCrisPartnerStore,
    loadCrisPartner,
    patchCrisPartnerMap,
    selectCurrentPartner,
} from '../cris.slice';

export const CrisPartnerRouter: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const { id: partnerId } = useParams<{ id: string }>();
    const partner = useSelector(selectCurrentPartner);
    const jsonSpacing = 2;

    useEffect(() => {
        dispatch(loadCrisPartner(partnerId));
    }, [dispatch, partnerId]);

    if (!partner) {
        return GeneralConstants.RENDER_NOTHING;
    }

    const columns: Array<GridColDef<PartnerMap>> = [
        {
            field: '',
            headerName: 'Unlink',
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params) => (
                <RequireRole roles={Role.CRIS_MANAGER}>
                    <IconButton
                        onClick={() => {
                            if (window.confirm('Are you sure you want to unlink this?')) {
                                dispatch(deleteCrisPartnerMap(params.row, partner));
                            }
                        }}
                        size='large'
                    >
                        <Icon>delete</Icon>
                    </IconButton>
                </RequireRole>
            ),
        },
        {
            field: 'id',
            headerName: 'ID',
            width: 300,
        },
        {
            field: 'storeName',
            headerName: 'Store Name',
            width: 250,
            renderCell: (data) => {
                const store = data.row.store;
                const statusIcon = data.row.inRedshift ? (
                    <Check style={{ color: 'green' }} />
                ) : (
                    <Close style={{ color: 'red' }} />
                );

                return (
                    <div style={{ display: 'flex', width: '100%' }}>
                        <RouterLink to={`/cris/stores/${store.id}`}>{store.name}</RouterLink>
                        <Tooltip title='Redshift Status Indicator'>
                            <div style={{ marginLeft: 'auto' }}> {statusIcon}</div>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: 'locationGroup',
            headerName: 'Location Group',
            width: 300,
            renderCell: (params: GridRenderCellParams<PartnerMap, PartnerMap['locationGroup']>) => {
                if (!params.value) {
                    return '(none)';
                }

                const group = params.value;

                return (
                    <Tooltip title={<pre>{JSON.stringify(group, undefined, jsonSpacing)}</pre>}>
                        <span>{group.id}</span>
                    </Tooltip>
                );
            },
            editable: true,
            renderEditCell: (params) => {
                const api = params.api;
                const id = params.id;

                return (
                    <LocationGroupPicker
                        name='locationGroup'
                        size='small'
                        fullWidth
                        variant='outlined'
                        value={params.row}
                        onChange={(evt, field, value) => {
                            void api.setEditCellValue({
                                id,
                                field,
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                value: value!,
                            });
                            api.stopCellEditMode({ id, field });
                        }}
                    />
                );
            },
        },
        {
            field: 'mcc',
            headerName: 'MCC',
            disableColumnMenu: true,
            width: 100,
            editable: true,
            renderEditCell: (params: GridRenderEditCellParams<PartnerMap, PartnerMap['mcc']>) => {
                const api = params.api;
                const id = params.id;
                const field = params.field;
                const value = params.value;

                return (
                    <InputBase
                        autoFocus
                        fullWidth
                        className='MuiDataGrid-editInputCell'
                        value={value}
                        onBlur={() => {
                            api.stopCellEditMode({ id, field });
                        }}
                        onChange={(evt) => {
                            void api.setEditCellValue({ id, field, value: evt.target.value });
                        }}
                    />
                );
            },
        },
        {
            field: 'midChannel',
            headerName: 'Channel',
            width: 140,
            editable: true,
            type: 'string',
            valueFormatter: (params: GridValueFormatterParams<PartnerMap['midChannel']>) => params.value || '(any)',
            renderEditCell: (params: GridRenderEditCellParams<PartnerMap, PartnerMap['midChannel']>) => {
                const api = params.api;
                const id = params.id;
                const field = params.field;
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const value = params.value!;

                return (
                    <ChannelSelector
                        value={value}
                        emptyLabel='(any)'
                        autofocus
                        onChange={(evt) => {
                            void api.setEditCellValue({ id, field, value: evt.target.value });
                            api.stopCellEditMode({ id, field });
                        }}
                    />
                );
            },
        },
        {
            field: 'storeId',
            headerName: 'Store Id',
            width: 110,
            disableColumnMenu: true,
            valueGetter: (params) => params.row.store.id,
        },
        {
            field: 'brandName',
            headerName: 'Brand Name',
            width: 250,
            valueGetter: (params) => params.row.store.brand?.name,
        },
        {
            field: 'brandId',
            headerName: 'Brand Id',
            width: 110,
            disableColumnMenu: true,
            valueGetter: (params) => params.row.store.brand?.id,
        },
    ];

    return (
        <div>
            <Typography variant='h4'>
                Partner: {partner.name} (ID: {partner.id})
            </Typography>
            <Typography variant='subtitle1'>
                Category: {partner.category ?? '[none]'} / Active: {partner.active ? 'Y' : 'N'} / Display:{' '}
                {partner.display ? 'Y' : 'N'} / Online: {partner.online ? 'Y' : 'N'}
            </Typography>

            <Box display='flex' my={1} alignItems='center'>
                <Typography variant='h5'>Linked CRIS Stores </Typography>
                <Box flexGrow='1' />
                <RequireRole roles={Role.PARTNER_MAPPING_RW}>
                    <Typography>Select a store to link it: </Typography>
                    <Box flexBasis='20rem' mx={1}>
                        <StorePicker
                            size='small'
                            up
                            label='Link Store'
                            onChange={(evt, store) => {
                                const storeName = store?.name;
                                const partnerName = partner.name;
                                if (!store) {
                                    return;
                                }
                                dialogService.showConfirm(
                                    {
                                        message: `Proceed with linking store ${storeName ?? ''} to partner ${
                                            partnerName ?? ''
                                        }?`,
                                        confirmText: 'OK',
                                    },
                                    () => dispatch(linkCrisPartnerStore(partner, store)),
                                );
                            }}
                        />
                    </Box>
                </RequireRole>
            </Box>
            <Box mb={1}>
                <Typography variant='body1'>Double click Location/MCC/Channel to modify them.</Typography>
            </Box>
            <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                <DataGridPro
                    rows={partner.storeMaps}
                    columns={columns}
                    paginationModel={{
                        pageSize: 25,
                        page: 0,
                    }}
                    disableRowSelectionOnClick
                    autoHeight
                    processRowUpdate={(updatedRow, originalRow) => {
                        if (updatedRow.locationGroup !== originalRow.locationGroup) {
                            dispatch(
                                patchCrisPartnerMap(
                                    { id: updatedRow.id },
                                    partner,
                                    'locationGroup',
                                    updatedRow.locationGroup,
                                ),
                            );
                        }
                        if (updatedRow.mcc !== originalRow.mcc) {
                            dispatch(patchCrisPartnerMap({ id: updatedRow.id }, partner, 'mcc', updatedRow.mcc));
                        }
                        if (updatedRow.midChannel !== originalRow.midChannel) {
                            dispatch(
                                patchCrisPartnerMap(
                                    { id: updatedRow.id },
                                    partner,
                                    'midChannel',
                                    updatedRow.midChannel,
                                ),
                            );
                        }

                        return updatedRow;
                    }}
                    density='compact'
                />
            </Box>
        </div>
    );
};
